import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { guardarConfirmado, reportarErrorGuardado, consultarEstadoIncierto } from "@/lib/guardado";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Field, DateField } from "@/components/forms/fields";
import { etiquetaTipoDispositivo } from "@/lib/dispositivoLabel";

const TIPOS = ["MILESIGHT_WS101", "Detector térmico", "Radar de caída", "Radar de cama", "Botón de pánico"];

const CAPACIDADES = [
  { v: "caida", l: "Caída" },
  { v: "panico", l: "Pánico" },
  { v: "presencia", l: "Presencia" },
  { v: "audio", l: "Audio" },
  { v: "cama", l: "Cama" },
];

const empty = {
  dev_eui: "",
  ws101_fport: 85,
  ws101_intervalo_min: null,
  identificador_evento: "",
  modelo: "",
  numero_serie: "",
  tipo: "",
  ubicacion: "",
  mac_address: "",
  conectividad: "En línea",
  registro: "Activo",
  fecha_instalacion: "",
  notas: "",
  personas_monitoreadas: [],
  capacidades: [],
  zona_id: null,
  cama_id: null,
};

export default function DeviceForm({ open, onOpenChange, device, sitioId, personas, zonas = [], camas = [], onSaved }) {
  const [f, setF] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [errorGuardado, setErrorGuardado] = useState(null);
  const enviandoRef = useRef(false);
  const cargadoRef = useRef(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  // Aviso FACTUAL no bloqueante: la cama elegida pertenece a otra zona del mismo sitio.
  const camaSel = camas.find((c) => c.id === f.cama_id);
  const avisoZonaCama = f.zona_id && camaSel && camaSel.zona_id !== f.zona_id
    ? "La cama seleccionada pertenece a otra habitación/zona de este sitio. Se guardará tal cual: verifica la instalación física."
    : null;

  useEffect(() => {
    if (!open) { cargadoRef.current = null; return; }
    const clave = device?.id || "nuevo";
    if (cargadoRef.current === clave) return;
    cargadoRef.current = clave;
    setF(device ? { ...empty, ...device } : empty);
  }, [open, device]);

  const toggderPersona = (pid) => {
    setF((p) => {
      const has = (p.personas_monitoreadas || []).includes(pid);
      return {
        ...p,
        personas_monitoreadas: has
          ? p.personas_monitoreadas.filter((x) => x !== pid)
          : p.tipo === "MILESIGHT_WS101" ? [pid] : [...(p.personas_monitoreadas || []), pid],
      };
    });
  };

  const toggleCapacidad = (cap) => {
    setF((p) => {
      const has = (p.capacidades || []).includes(cap);
      return {
        ...p,
        capacidades: has
          ? p.capacidades.filter((x) => x !== cap)
          : [...(p.capacidades || []), cap],
      };
    });
  };

  const save = async () => {
    // El guard debe ser SINCRÓNICO: `saving` es estado y dos clics en el mismo ciclo veían el
    // valor anterior, así que un doble clic rápido creaba dos registros.
    if (enviandoRef.current) return;
    enviandoRef.current = true;
    setSaving(true);
    setErrorGuardado(null);
    try {
      const payload = { ...f, sitio_id: sitioId };
      await guardarConfirmado({
        escribir: () => (device?.id ? api.put(`/dispositivos/${device.id}`, payload) : api.post("/dispositivos", payload)),
        refrescar: (res) => {
          const av = res?.data?.avisos_relacion || [];
          if (av.length) toast.warning(av[0]);
          return onSaved ? onSaved(res.data) : undefined;
        },
        exito: "Dispositivo guardado.",
      });
      onOpenChange(false);
    } catch (e) {
      // La captura se conserva: el diálogo NO se cierra y el motivo queda visible.
      setErrorGuardado(reportarErrorGuardado(e, { consultarEstado: onSaved }));
    } finally {
      enviandoRef.current = false;
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#012472]">
            {device?.id ? "Editar dispositivo" : "Nuevo dispositivo"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {device?.id && (
            <p className="text-xs text-slate-400">ID interno: {device.id}</p>
          )}
          <Field label="Identificador de evento" hint="Código con el que llega el evento desde el sensor. Cualquier formato de texto.">
            <Input data-testid="device-identificador-input" className="rounded-xl" value={f.identificador_evento} onChange={(e) => set("identificador_evento", e.target.value)} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Modelo">
              <Input className="rounded-xl" value={f.modelo} onChange={(e) => set("modelo", e.target.value)} />
            </Field>
            <Field label="Número de serie">
              <Input className="rounded-xl" value={f.numero_serie} onChange={(e) => set("numero_serie", e.target.value)} />
            </Field>
          </div>
          {f.tipo === "MILESIGHT_WS101" && <div data-testid="ws101-config" className="space-y-3">
            <Field label="DevEUI WS101"><Input data-testid="ws101-deveui-input" className="rounded-xl" value={f.dev_eui || ""} maxLength={16} onChange={e => set("dev_eui", e.target.value)} /></Field>
            <Field label="FPort configurado"><Input data-testid="ws101-fport-input" className="rounded-xl" type="number" min={1} max={223} value={f.ws101_fport} onChange={e => set("ws101_fport", Number(e.target.value))} /></Field>
            <Field label="Intervalo real de reporte (minutos)" hint="Dejar vacío si no se ha leído del equipo. Valor de fábrica documentado: 1080 min."><Input data-testid="ws101-intervalo-input" className="rounded-xl" type="number" min={1} max={1080} value={f.ws101_intervalo_min ?? ""} onChange={e => set("ws101_intervalo_min", e.target.value === "" ? null : Number(e.target.value))} /></Field>
            <p className="text-sm">WS101: ubicación seca (IP30), pila reemplazable. Seleccionar como máximo una persona; sin persona, la solicitud corresponde a la ubicación. Guardar esta ficha no configura el gateway.</p>
          </div>}
          <Field label="MAC address" hint="Necesaria para sensores Hikvision HM (ingesta directa). Ej. a4:4c:62:46:3f:89 — se normaliza automáticamente.">
            <Input data-testid="device-mac-input" className="rounded-xl" value={f.mac_address || ""} onChange={(e) => set("mac_address", e.target.value)} placeholder="a4:4c:62:46:3f:89" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tipo">
              <Select value={f.tipo || ""} onValueChange={(v) => set("tipo", v)}>
                <SelectTrigger data-testid="device-tipo-select" className="rounded-xl"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                <SelectContent>
                  {TIPOS.map((t) => <SelectItem key={t} data-testid={`device-tipo-${t}`} value={t}>{etiquetaTipoDispositivo(t)}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Ubicación" hint="Ej. Recámara principal, Baño">
              <Input data-testid="device-ubicacion-input" className="rounded-xl" value={f.ubicacion} onChange={(e) => set("ubicacion", e.target.value)} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Estado de conectividad">
              <Select value={f.conectividad} onValueChange={(v) => set("conectividad", v)}>
                <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="En línea">En línea</SelectItem>
                  <SelectItem value="Fuera de línea">Fuera de línea</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Estado de registro">
              <Select value={f.registro} onValueChange={(v) => set("registro", v)}>
                <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Activo">Activo</SelectItem>
                  <SelectItem value="Retirado">Retirado</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Fecha de instalación">
            <DateField testId="device-fecha-instalacion" value={f.fecha_instalacion} onChange={(v) => set("fecha_instalacion", v)} />
          </Field>
          {/* DATA-005: relaciones ESTRUCTURADAS opcionales, siempre DENTRO del sitio. "Sin
              asignar" envía null y limpia explícitamente; no enviar el campo lo conserva. Zona y
              cama son independientes: si la cama pertenece a otra zona se avisa sin bloquear. */}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Habitación / zona (opcional)">
              <Select value={f.zona_id || "__none__"} onValueChange={(v) => set("zona_id", v === "__none__" ? null : v)}>
                <SelectTrigger className="rounded-xl" data-testid="device-zona-select"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sin asignar</SelectItem>
                  {zonas.filter((z) => z.estado !== "Inactivo").map((z) => (
                    <SelectItem key={z.id} value={z.id}>{z.nombre || "(sin nombre)"}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Cama (opcional)">
              <Select value={f.cama_id || "__none__"} onValueChange={(v) => set("cama_id", v === "__none__" ? null : v)}>
                <SelectTrigger className="rounded-xl" data-testid="device-cama-select"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sin asignar</SelectItem>
                  {camas.filter((c) => c.estado !== "Inactivo").map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.etiqueta || c.nombre || "(sin etiqueta)"}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>
          {avisoZonaCama && (
            <p data-testid="device-aviso-zona-cama" className="text-xs text-amber-700">{avisoZonaCama}</p>
          )}
          <Field label="¿A qué persona(s) monitorea?" hint={f.tipo === "MILESIGHT_WS101" ? "Una persona explícita o ninguna para ayuda por ubicación." : "Puede monitorear a una o varias personas."}>
            <div className="space-y-2 rounded-xl border border-gray-100 p-3">
              {(personas || []).length === 0 && <p className="text-sm text-slate-400">No hay personas registradas en este sitio.</p>}
              {(personas || []).map((p) => (
                <label key={p.id} className="flex items-center gap-2 text-sm text-slate-700">
                  <Checkbox
                    checked={(f.personas_monitoreadas || []).includes(p.id)}
                    onCheckedChange={() => toggderPersona(p.id)}
                    data-testid={`device-persona-${p.id}`}
                  />
                  {p.nombre_completo || "(sin nombre)"}
                </label>
              ))}
            </div>
          </Field>
          <Field label="Capacidades del dispositivo" hint="Qué puede detectar. 'Caída' y 'Pánico' habilitan la vigilancia de cobertura crítica (Bloque C).">
            <div className="grid grid-cols-2 gap-2 rounded-xl border border-gray-100 p-3">
              {CAPACIDADES.map((c) => (
                <label key={c.v} className="flex items-center gap-2 text-sm text-slate-700">
                  <Checkbox
                    checked={(f.capacidades || []).includes(c.v)}
                    onCheckedChange={() => toggleCapacidad(c.v)}
                    data-testid={`device-cap-${c.v}`}
                  />
                  {c.l}
                </label>
              ))}
            </div>
          </Field>
          <Field label="Notas">
            <Textarea className="rounded-xl" value={f.notas} onChange={(e) => set("notas", e.target.value)} />
          </Field>
        </div>
        <DialogFooter>
          {errorGuardado && (
            <div role="alert" data-testid="device-error-banner" className="w-full rounded-xl border border-red-200 bg-red-50 p-3 text-left">
              <p className="text-sm font-semibold text-red-700">{errorGuardado.titulo}</p>
              <p className="text-xs text-red-600 mt-0.5">{errorGuardado.detalle}</p>
              {errorGuardado.incierto && (
                <Button variant="outline" size="sm" data-testid="device-consultar-estado" className="mt-2 rounded-lg"
                  onClick={() => consultarEstadoIncierto(() => onSaved && onSaved())}>Consultar estado</Button>
              )}
            </div>
          )}
          <Button variant="outline" className="rounded-xl" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button data-testid="device-guardar-button" disabled={saving} aria-busy={saving} className={`rounded-xl bg-[#01CBE3] hover:bg-[#01B5CB] text-white ${saving ? "pointer-events-none" : ""}`} onClick={save}>
            {saving ? "Guardando…" : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
